import { CSSProperties, useContext, useEffect, useRef, useState } from 'react'
import { MastData, SignalTypes } from '../../../client'
import MastContext from '../../../contexts/MastContext'
import calcVerticalHeight from '../../../utils/calcVerticalHeight'
import ColorScale, { plot_color_scale_200 } from '../../../utils/colorScale'
import { defaultConfig, layoutTemplate } from '../../../utils/plotly'
import { SetStateGeneric } from '../../../utils/SetStateGeneric'
import { sortByDateKey } from '../../../utils/sort'
import Plot from '../../shared/Plot'
import useData from '../useData'

const config = { ...defaultConfig }
const style: CSSProperties = {
  minHeight: '10rem',
  height: calcVerticalHeight({ gridSize: 4, elementSize: 3 }),
  width: '100%',
}

const PlotSignal = ({
  dtIn,
  dtFin,
  signalType,
  revision,
  setDtIn,
  setDtFin,
}: {
  dtIn: Date
  dtFin: Date
  signalType: SignalTypes
  revision: number
  setDtIn: SetStateGeneric<Date>
  setDtFin: SetStateGeneric<Date>
}) => {
  const { mast, sensors: allSensors } = useContext(MastContext)
  const { mastDataBySensor } = useData(dtIn, dtFin)
  const sensors = allSensors.filter(s => signalType.id === s.signal_type_id)

  const [yaxisRange, setYaxisRange] = useState<[number, number]>([
    signalType.min_graph,
    signalType.max_graph,
  ])

  const layout = {
    template: layoutTemplate,
    autorange: false,
    yaxis: { range: yaxisRange, autorange: true },
    xaxis: { range: [dtIn, dtFin], autorange: true },
  }

  const data = sensors
    .flatMap((s, i) => {
      const _mastData = mastDataBySensor.get(s.id)
      if (!_mastData) {
        return null
      }
      const mastData = sortByDateKey<MastData>(_mastData, 'ts', 'asc')
      const x = mastData.map(d => d.ts)
      const avg: number[] = []
      const lowerBound: number | null = s.min_estimate
      const upperBound: number | null = s.max_estimate
      const avgEstimated: number | null = s.avg_estimate
      const sensorColor: string = plot_color_scale_200[i]

      mastData.forEach(md => {
        avg.push(md.avg)
      })

      if (lowerBound == null || upperBound == null || avgEstimated == null) {
        return [
          {
            x,
            y: avg,
            type: 'scatter',
            mode: 'line',
            name: `${s.label} - AVG`,
            marker: { color: sensorColor },
          },
        ]
      }

      return [
        {
          x,
          y: avg,
          type: 'scatter',
          mode: 'line',
          name: `${s.label} - AVG`,
          marker: { color: sensorColor },
        },
        {
          x: [dtIn, dtFin],
          y: [lowerBound, lowerBound],
          type: 'scatter',
          mode: 'lines',
          marker: { color: ColorScale.vermelho_puro },
          name: 'Min Estimated',
        },
        {
          x: [dtIn, dtFin],
          y: [upperBound, upperBound],
          type: 'scatter',
          mode: 'lines',
          marker: { color: ColorScale.vermelho_puro },
          name: 'Max Estimated',
        },
        {
          x: [dtIn, dtFin],
          y: [avgEstimated, avgEstimated],
          type: 'scatter',
          mode: 'lines',
          marker: { color: ColorScale.cinza_medio_claro },
          name: 'Avg Estimated',
        },
      ]
    })
    .filter(d => d)

  const lastXRef = useRef<{ start: number; end: number } | null>(null)
  const lastYRef = useRef<{ start: number; end: number } | null>(null)
  const toMs = (v: any) => (typeof v === 'number' ? v : new Date(v).getTime())

  useEffect(() => {
    if (!lastXRef.current) lastXRef.current = { start: dtIn.getTime(), end: dtFin.getTime() }
    if (!lastYRef.current && yaxisRange?.length === 2)
      lastYRef.current = { start: yaxisRange[0], end: yaxisRange[1] }
  }, [])

  return (
    <div style={style}>
      <Plot
        key={mast.id}
        useResizeHandler
        data={data}
        layout={layout}
        config={config}
        style={style}
        revision={revision}
        onRelayout={(e: any) => {
          // --- X ---
          const x0r = e['xaxis.range[0]']
          const x1r = e['xaxis.range[1]']

          if (x0r != null || x1r != null) {
            const prev = lastXRef.current ?? { start: dtIn.getTime(), end: dtFin.getTime() }
            const nx0 = x0r != null ? toMs(x0r) : prev.start
            const nx1 = x1r != null ? toMs(x1r) : prev.end
            const prevDiff = prev.end - prev.start
            const newDiff = nx1 - nx0

            if (newDiff > prevDiff) {
              setDtIn(new Date(nx0))
              setDtFin(new Date(nx1))
            }

            lastXRef.current = { start: nx0, end: nx1 }
          }

          // --- Y ---
          const y0r = e['yaxis.range[0]']
          const y1r = e['yaxis.range[1]']

          if (y0r != null || y1r != null) {
            const prevY = lastYRef.current ?? { start: yaxisRange[0], end: yaxisRange[1] }
            const ny0 = y0r != null ? Number(y0r) : prevY.start
            const ny1 = y1r != null ? Number(y1r) : prevY.end

            const prevYDiff = prevY.end - prevY.start
            const newYDiff = ny1 - ny0

            if (newYDiff > prevYDiff) setYaxisRange([ny0, ny1])

            lastYRef.current = { start: ny0, end: ny1 }
          }
        }}
      />
    </div>
  )
}
export default PlotSignal
